import React, { useRef } from 'react';
import MuiButton from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { alpha, styled } from '@mui/material/styles';

import { spacingPx } from '../theme/tokens.spacing';
import { radius } from '../theme/tokens.radius';
import { typography as typeTokens } from '../theme/tokens.typography';
import { ThemeColor } from '../theme';
import { colors } from '../theme/tokens.colors';
import { uikitPalette } from '../theme/uikitPalette';

const INLINE_FLEX = 'inline-flex';

export interface UIKitButtonProps {
  htmlId?: string;
  className?: string;
  text: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  disabled?: boolean;
  loading?: boolean;
  type?: 'button' | 'submit' | 'reset';
  title?: string;
  variant?: 'flat' | 'stroke' | 'link';
  color?: ThemeColor;
  size?: 'large' | 'small';
  style?: React.CSSProperties;
  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;
}

const StyledButton = styled(MuiButton, {
  shouldForwardProp: (prop) => prop !== 'uiColor' && prop !== 'uiVariant' && prop !== 'uiSize',
})<{ uiColor: ThemeColor; uiVariant: 'flat' | 'stroke' | 'link'; uiSize: 'large' | 'small' }>(
  ({ uiColor, uiVariant, uiSize }) => {
    const palette = uikitPalette[uiColor];

    let variantStyles: Record<string, unknown>;

    // FLAT / CONTAINED
    if (uiVariant === 'flat') {
      variantStyles = {
        backgroundColor: palette.main,
        color: palette.on,
        '&:hover': {
          backgroundColor: alpha(palette.main, 0.8),
          boxShadow: 'none',
        },
        '&:active, &:focus': {
          backgroundColor: alpha(palette.main, 0.65),
          boxShadow: 'none',
        },
        '&.Mui-disabled': {
          backgroundColor: colors.neutralVariant[200],
          color: colors.neutralVariant[600],
        },
      };
    } else if (uiVariant === 'stroke') {
      // STROKE / OUTLINED
      variantStyles = {
        backgroundColor: 'transparent',
        border: `1px solid ${palette.main}`,
        color: palette.main,
        '&:hover': {
          backgroundColor: alpha(palette.main, 0.08),
        },
        '&:active, &:focus': {
          backgroundColor: alpha(palette.main, 0.16),
        },
        '&.Mui-disabled': {
          borderColor: colors.neutralVariant[50],
          color: colors.neutralVariant[500],
        },
      };
    } else {
      // LINK / TEXT
      variantStyles = {
        backgroundColor: 'transparent',
        border: 'none',
        color: palette.main,
        '&:hover': {
          backgroundColor: alpha(palette.main, 0.08),
        },
        '&:active, &:focus': {
          backgroundColor: alpha(palette.main, 0.16),
        },
        '&.Mui-disabled': {
          color: colors.neutralVariant[500],
        },
      };
    }

    return {
      borderRadius: radius.full,
      textTransform: 'none' as const,
      boxShadow: 'none',
      ...variantStyles,
      ...(uiSize === 'small'
        ? {
            paddingTop: spacingPx[4],
            paddingBottom: spacingPx[4],
            paddingLeft: spacingPx[12],
            paddingRight: spacingPx[12],
            ...typeTokens.buttonSmall.regular,
          }
        : {
            paddingTop: spacingPx[8],
            paddingBottom: spacingPx[8],
            paddingLeft: spacingPx[16],
            paddingRight: spacingPx[16],
            ...typeTokens.button.regular,
          }),
    };
  }
);

const Content = styled('div')({
  display: INLINE_FLEX,
  alignItems: 'center',
  justifyContent: 'center',
});

const IconLeft = styled('span')({
  marginRight: spacingPx[8],
  display: INLINE_FLEX,
  alignItems: 'center',
});

const IconRight = styled('span')({
  marginLeft: spacingPx[8],
  display: INLINE_FLEX,
  alignItems: 'center',
});

const Spinner = styled(CircularProgress)({
  color: 'inherit',
});

const UIKitButton: React.FC<UIKitButtonProps> = ({
  htmlId,
  className,
  text,
  onClick,
  disabled,
  loading,
  type = 'button',
  variant = 'flat',
  color = 'primary',
  size = 'large',
  style,
  iconLeft,
  iconRight,
  title,
}) => {
  const btnRef = useRef<HTMLButtonElement | null>(null);

  // Usamos los variants de MUI sólo como "modo" visual,
  // pero los colores reales vienen de nuestras clases.
  const getMuiVariant = (): 'contained' | 'outlined' | 'text' => {
    if (variant === 'flat') {
      return 'contained';
    }
    if (variant === 'stroke') {
      return 'outlined';
    }
    return 'text';
  };
  const muiVariant = getMuiVariant();

  const isDisabled = disabled || loading;

  const handleClick: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    e.stopPropagation();
    if (btnRef.current) {
      btnRef.current.blur();
    }

    if (!isDisabled && onClick) {
      onClick(e);
    }
  };

  return (
    <StyledButton
      id={htmlId}
      ref={btnRef}
      variant={muiVariant}
      uiColor={color}
      uiVariant={variant}
      uiSize={size}
      disabled={isDisabled}
      type={type}
      onClick={handleClick}
      className={className}
      title={title}
      style={style}
    >
      <Content>
        {loading && iconLeft && (
          <Spinner size={18} thickness={4} sx={{ marginRight: `${spacingPx[8]}px` }} />
        )}

        {!loading && iconLeft && <IconLeft>{iconLeft}</IconLeft>}

        <span>{text}</span>

        {!loading && iconRight && <IconRight>{iconRight}</IconRight>}

        {loading && !iconLeft && (
          <Spinner size={18} thickness={4} sx={{ marginLeft: `${spacingPx[8]}px` }} />
        )}
      </Content>
    </StyledButton>
  );
};

export default UIKitButton;
